import React, { useState, useEffect, useRef, useCallback } from 'react';

// ── Données des 10 pays SP-API ─────────────────────────────────────────────
const SP_API_COUNTRIES = [
  { code: 'FR', name: 'France',      flag: '🇫🇷' },
  { code: 'UK', name: 'Royaume-Uni', flag: '🇬🇧' },
  { code: 'DE', name: 'Allemagne',   flag: '🇩🇪' },
  { code: 'ES', name: 'Espagne',     flag: '🇪🇸' },
  { code: 'IT', name: 'Italie',      flag: '🇮🇹' },
  { code: 'PL', name: 'Pologne',     flag: '🇵🇱' },
  { code: 'SE', name: 'Suède',       flag: '🇸🇪' },
  { code: 'IE', name: 'Irlande',    flag: '🇮🇪' },
  { code: 'BE', name: 'Belgique',    flag: '🇧🇪' },
  { code: 'NL', name: 'Pays-Bas',   flag: '🇳🇱' },
];

// ── Helpers ────────────────────────────────────────────────────────────────
function fmt(date) { return date.toISOString().slice(0, 10); }

function defaultDates() {
  const yesterday = new Date(); yesterday.setDate(yesterday.getDate() - 1);
  const weekAgo   = new Date(); weekAgo.setDate(weekAgo.getDate() - 7);
  return { start: fmt(weekAgo), end: fmt(yesterday) };
}

function jobIsDone(job) {
  if (!job) return false;
  return Object.values(job.marketplaces).every(s => s.status === 'done' || s.status === 'error');
}

// ── Badge de statut ────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const cfg = {
    pending: { bg: 'bg-gray-100', text: 'text-gray-500', label: 'En attente' },
    running: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'En cours…' },
    done:    { bg: 'bg-green-100', text: 'text-green-700', label: 'Terminé ✅' },
    error:   { bg: 'bg-red-100',   text: 'text-red-700',   label: 'Erreur ❌' },
  }[status] || { bg: 'bg-gray-100', text: 'text-gray-500', label: status };

  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${cfg.bg} ${cfg.text}`}>
      {cfg.label}
    </span>
  );
}

// ── Composant principal ────────────────────────────────────────────────────
function AutoImport() {
  const { start: defStart, end: defEnd } = defaultDates();
  const [startDate,       setStartDate]       = useState(defStart);
  const [endDate,         setEndDate]         = useState(defEnd);
  const [dateGranularity, setDateGranularity] = useState('DAY');
  const [asinGranularity, setAsinGranularity] = useState('SKU');

  const [jobId,       setJobId]       = useState(null);
  const [job,         setJob]         = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState(null);
  const [lastCronRun, setLastCronRun] = useState(undefined); // undefined=chargement, null=aucun

  const pollingRef = useRef(null);

  // ── Polling ──────────────────────────────────────────────────────────────
  const pollStatus = useCallback(async (id) => {
    try {
      const res  = await fetch(`/api/auto-import/status/${id}`);
      if (!res.ok) return;
      const data = await res.json();
      setJob(data);
      if (jobIsDone(data)) stopPolling();
    } catch {
      // ignore transient network errors
    }
  }, []); // eslint-disable-line

  const startPolling = useCallback((id) => {
    stopPolling();
    pollingRef.current = setInterval(() => pollStatus(id), 5000);
  }, [pollStatus]);

  function stopPolling() {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }

  useEffect(() => () => stopPolling(), []);

  useEffect(() => {
    fetch('/api/cron/last-run')
      .then(r => r.json())
      .then(data => setLastCronRun(data))
      .catch(() => setLastCronRun(null));
  }, []);

  // ── Lancer l'import ──────────────────────────────────────────────────────
  const handleStart = async () => {
    if (!startDate || !endDate) {
      setError('Sélectionnez une date de début et de fin.');
      return;
    }
    if (startDate > endDate) {
      setError('La date de début doit être antérieure à la date de fin.');
      return;
    }
    setError(null);
    setLoading(true);
    stopPolling();
    setJob(null);

    try {
      const res = await fetch('/api/auto-import/start', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ startDate, endDate, dateGranularity, asinGranularity }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erreur serveur');

      setJobId(data.jobId);
      setJob(data);
      startPolling(data.jobId);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Réessayer un pays ────────────────────────────────────────────────────
  const handleRetry = async (code) => {
    if (!jobId) return;
    try {
      const res = await fetch('/api/auto-import/retry', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ jobId, marketplace: code }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erreur');

      // Relancer le polling si pas déjà en cours
      if (!pollingRef.current) startPolling(jobId);
      // Mise à jour optimiste du statut local
      setJob(prev => ({
        ...prev,
        marketplaces: {
          ...prev.marketplaces,
          [code]: { ...prev.marketplaces[code], status: 'pending', error: null, rows: 0 },
        },
      }));
    } catch (err) {
      alert(`Erreur retry ${code} : ${err.message}`);
    }
  };

  // ── Résumé post-import ───────────────────────────────────────────────────
  const doneCount    = job ? Object.values(job.marketplaces).filter(s => s.status === 'done').length  : 0;
  const errorCount   = job ? Object.values(job.marketplaces).filter(s => s.status === 'error').length : 0;
  const totalRows    = job ? Object.values(job.marketplaces).reduce((s, m) => s + (m.rows || 0), 0)   : 0;
  const isFinished   = job && jobIsDone(job);

  // ── Rendu ────────────────────────────────────────────────────────────────
  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h2 className="text-xl font-bold text-gray-800 mb-1">Import automatique — Amazon SP-API</h2>
      <p className="text-sm text-gray-500 mb-6">
        Récupère les rapports <em>Sales & Traffic</em> pour les 10 marketplaces européennes via l'API Amazon.
        L'import est séquentiel ; chaque rapport peut prendre 2 à 20 minutes.
      </p>

      {/* ── Dernier import automatique (cron) ──────────────────────────── */}
      {lastCronRun !== undefined && (
        <div className={`rounded-lg border px-4 py-3 mb-6 text-sm ${
          lastCronRun === null
            ? 'bg-gray-50 border-gray-200 text-gray-500'
            : lastCronRun.status === 'success'
              ? 'bg-green-50 border-green-200 text-green-800'
              : lastCronRun.status === 'partial'
                ? 'bg-amber-50 border-amber-200 text-amber-800'
                : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          {lastCronRun === null ? (
            <span>Aucun import automatique planifié n'a encore été exécuté.</span>
          ) : (() => {
            const fmtD = (iso) => iso ? iso.split('-').reverse().join('/') : '';
            const { j1, j4 } = lastCronRun.results;
            const lineFor = (slot) => {
              if (!slot) return null;
              const ok  = slot.countries.filter(r => r.success).length;
              const err = slot.countries.filter(r => !r.success).map(r => r.country);
              return ok === 10
                ? `✅ ${ok}/10`
                : err.length === 10
                  ? `❌ tous en erreur`
                  : `⚠️ ${ok}/10 — ${err.join(', ')} en erreur`;
            };
            return (
              <>
                <span className="font-medium">Dernier import automatique : </span>
                {new Date(lastCronRun.ran_at).toLocaleString('fr-FR', {
                  day: '2-digit', month: '2-digit', year: 'numeric',
                  hour: '2-digit', minute: '2-digit',
                })}
                <span className="ml-3">
                  J-1 <strong>({fmtD(j1?.date)})</strong> {lineFor(j1)}
                  {j4 && <span className="ml-3">J-4 <strong>({fmtD(j4?.date)})</strong> {lineFor(j4)}</span>}
                </span>
              </>
            );
          })()}
        </div>
      )}

      {/* ── Formulaire ─────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Date de début</label>
            <input
              type="date"
              value={startDate}
              max={endDate}
              onChange={e => setStartDate(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Date de fin</label>
            <input
              type="date"
              value={endDate}
              min={startDate}
              max={fmt(new Date())}
              onChange={e => setEndDate(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Granularité date</label>
            <select
              value={dateGranularity}
              onChange={e => setDateGranularity(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
            >
              <option value="DAY">Jour (DAY)</option>
              <option value="WEEK">Semaine (WEEK)</option>
              <option value="MONTH">Mois (MONTH)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Granularité ASIN</label>
            <select
              value={asinGranularity}
              onChange={e => setAsinGranularity(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
            >
              <option value="SKU">SKU</option>
              <option value="CHILD">Child ASIN</option>
              <option value="PARENT">Parent ASIN</option>
            </select>
          </div>
          <button
            onClick={handleStart}
            disabled={loading || (job && !isFinished && Object.values(job.marketplaces).some(s => s.status === 'running' || s.status === 'pending'))}
            className="px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white font-medium rounded-md text-sm transition-colors"
          >
            {loading ? 'Démarrage…' : '🔄 Importer les 10 pays'}
          </button>
        </div>

        {error && (
          <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
            {error}
          </div>
        )}
      </div>

      {/* ── Grille de statut ───────────────────────────────────────────── */}
      {job && (
        <>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-700">Progression</h3>
              {!isFinished && (
                <span className="text-xs text-gray-400 animate-pulse">Actualisation toutes les 5 s…</span>
              )}
              {isFinished && (
                <span className="text-xs font-medium text-green-600">
                  Import terminé — {doneCount}/10 pays OK, {totalRows} lignes insérées
                </span>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
              {SP_API_COUNTRIES.map(country => {
                const st = job.marketplaces[country.code] || { status: 'pending', rows: 0, error: null };
                return (
                  <div
                    key={country.code}
                    className={`rounded-lg border p-3 text-sm transition-colors ${
                      st.status === 'done'    ? 'border-green-200 bg-green-50' :
                      st.status === 'error'   ? 'border-red-200   bg-red-50'   :
                      st.status === 'running' ? 'border-blue-200  bg-blue-50'  :
                      'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-1 mb-1.5">
                      <span className="text-lg">{country.flag}</span>
                      <span className="font-semibold text-gray-700">{country.code}</span>
                    </div>
                    <StatusBadge status={st.status} />

                    {st.status === 'done' && (
                      <div className="mt-1.5 text-xs text-green-700 font-medium">
                        {st.rows} ligne{st.rows !== 1 ? 's' : ''}
                      </div>
                    )}

                    {st.status === 'running' && (
                      <div className="mt-1.5 text-xs text-blue-600">
                        Rapport en cours…
                      </div>
                    )}

                    {st.status === 'error' && (
                      <>
                        <div
                          className="mt-1.5 text-xs text-red-600 truncate"
                          title={st.error}
                        >
                          {st.error}
                        </div>
                        <button
                          onClick={() => handleRetry(country.code)}
                          className="mt-2 px-2 py-0.5 text-xs bg-red-600 hover:bg-red-700 text-white rounded font-medium"
                        >
                          ↻ Réessayer
                        </button>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Tableau récapitulatif ─────────────────────────────────── */}
          {isFinished && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <h3 className="font-semibold text-gray-700 mb-3">Récapitulatif</h3>
              <div className="grid grid-cols-3 gap-4 mb-4 text-center">
                <div className="bg-green-50 rounded-lg p-3">
                  <div className="text-2xl font-bold text-green-700">{doneCount}</div>
                  <div className="text-xs text-green-600">pays importés</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-3">
                  <div className="text-2xl font-bold text-blue-700">{totalRows}</div>
                  <div className="text-xs text-blue-600">lignes insérées</div>
                </div>
                <div className="bg-red-50 rounded-lg p-3">
                  <div className="text-2xl font-bold text-red-700">{errorCount}</div>
                  <div className="text-xs text-red-600">pays en erreur</div>
                </div>
              </div>

              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border border-gray-200 px-3 py-2 text-left">Pays</th>
                    <th className="border border-gray-200 px-3 py-2 text-center">Statut</th>
                    <th className="border border-gray-200 px-3 py-2 text-right">Lignes</th>
                    <th className="border border-gray-200 px-3 py-2 text-left">Détail / Erreur</th>
                  </tr>
                </thead>
                <tbody>
                  {SP_API_COUNTRIES.map(country => {
                    const st = job.marketplaces[country.code] || { status: 'pending', rows: 0, error: null };
                    return (
                      <tr key={country.code} className="hover:bg-gray-50">
                        <td className="border border-gray-200 px-3 py-2">
                          <span className="mr-1">{country.flag}</span>
                          <span className="font-medium">{country.name}</span>
                        </td>
                        <td className="border border-gray-200 px-3 py-2 text-center">
                          <StatusBadge status={st.status} />
                        </td>
                        <td className="border border-gray-200 px-3 py-2 text-right font-mono">
                          {st.rows || 0}
                        </td>
                        <td className="border border-gray-200 px-3 py-2 text-xs text-gray-500">
                          {st.error || (st.status === 'done' ? `session: auto_${job.options?.startDate}_${job.options?.endDate}_${country.code}` : '—')}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <p className="mt-3 text-xs text-gray-400">
                Les données importées sont stockées dans la base SQLite sous l'identifiant de session{' '}
                <code className="bg-gray-100 px-1 rounded">auto_{job.options?.startDate}_{job.options?.endDate}_&lt;PAYS&gt;</code>.
                Période couverte : {job.options?.startDate} → {job.options?.endDate}.
              </p>
            </div>
          )}
        </>
      )}

      {/* ── État initial (pas encore de job) ───────────────────────────── */}
      {!job && !loading && (
        <div className="bg-gray-50 border border-dashed border-gray-300 rounded-xl p-8 text-center text-gray-500">
          <div className="text-4xl mb-3">📡</div>
          <p className="font-medium">Aucun import en cours</p>
          <p className="text-sm mt-1">
            Sélectionnez une période puis cliquez sur <strong>"Importer les 10 pays"</strong>.
          </p>
          <p className="text-xs mt-2 text-gray-400">
            Prérequis : fichier <code>.env</code> configuré avec les credentials SP-API.
          </p>
        </div>
      )}
    </div>
  );
}

export default AutoImport;
